import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { budgetsAPI, glAccountsAPI } from '../api/client';
import apiClient from '../api/client';
import { usePermissions } from '../context/PermissionsContext';
import { useAuth } from '../context/AuthContext';
import { Plus, Edit2, Trash2, Search, DollarSign, Upload, Download, ArrowLeft, AlertTriangle, FileText, X, CheckCircle, TrendingUp, RefreshCw } from 'lucide-react';
import { isDepartmentManager } from '../utils/roleHelpers';
import useSortPage from '../hooks/useSortPage';
import TablePagination from '../components/TablePagination';
import SortableTh from '../components/SortableTh';

export default function BudgetLines() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const [budgets, setBudgets] = useState([]);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [budgetLines, setBudgetLines] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [departmentGLAccounts, setDepartmentGLAccounts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [vendors, setVendors] = useState([]);
  
  // Overflow state
  const [overflowLines, setOverflowLines] = useState([]);
  const [resolvingLineId, setResolvingLineId] = useState(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [reassigningLine, setReassigningLine] = useState(null);
  const [allBudgetLines, setAllBudgetLines] = useState([]);
  const [selectedTargetLineId, setSelectedTargetLineId] = useState('');
  const [reassigning, setReassigning] = useState(false);

  // Dimension filters
  const [dimensionFilter, setDimensionFilter] = useState('');
  const [costCentreFilter, setCostCentreFilter] = useState('');

  // Committed PO drill-down
  const [committedLine, setCommittedLine] = useState(null); // { line, pos }
  const [committedLoading, setCommittedLoading] = useState(false);

  const [formData, setFormData] = useState({
    glAccountId: '',
    description: '',
    budgetedAmount: '',
    departmentId: '',
    projectId: '',
    costCentre: '',
    lineType: 'one_off',
    spreadMonths: '',
    spreadStartDate: '',
    spreadData: '',
    // Subscription fields
    createSubscription: false,
    sub_vendorId: '',
    sub_contractName: '',
    sub_contractNumber: '',
    sub_paymentFrequency: 'Monthly',
    sub_paymentAmount: '',
    sub_paymentDayOfMonth: '',
    sub_startDate: '',
    sub_endDate: '',
    sub_renewalDate: '',
    sub_noticePeriodDays: '',
    sub_autoRenew: false
  });

  const canManage = hasPermission('manage_budgets') || hasPermission('budgets.manage.my-departments');

  const { sortBy: linesSortBy, sortDir: linesSortDir, handleSort: linesHandleSort, sortedData: linesSortedData, paginatedData: linesPagedData, paginationProps: linesPaginationProps } =
    useSortPage({ defaultSortBy: 'AccountCode', pageSize: 50 });

  const [linesSearch, setLinesSearch] = React.useState('');

  useEffect(() => {
    fetchBudgets();
    fetchGlAccounts();
    fetchDepartments();
    fetchProjects();
    fetchVendors();
  }, []);

  useEffect(() => {
    if (selectedBudget) {
      fetchBudgetLines(selectedBudget);
    }
  }, [selectedBudget]);

  const fetchBudgets = async () => {
    try {
      setLoading(true);
      let availableBudgets = [];
      
      // Department Managers only see their department budgets
      if (isDepartmentManager(user)) {
        const response = await budgetsAPI.getMyDepartmentBudgets();
        // Backend returns { budgets: [...] }
        availableBudgets = (response.data?.budgets || []).filter(b => b.Status !== 'Closed' && b.BudgetID);
      } else {
        // Other roles see all budgets
        const response = await budgetsAPI.getAll({ page: 1, pageSize: 100 });
        availableBudgets = (response.data.items || []).filter(b => b.Status !== 'Closed');
      }
      
      setBudgets(availableBudgets);
      
      // Check if budgetId is in URL params
      const budgetIdFromUrl = searchParams.get('budgetId');
      if (budgetIdFromUrl && availableBudgets.some(b => b.BudgetID === budgetIdFromUrl)) {
        setSelectedBudget(budgetIdFromUrl);
      } else if (availableBudgets.length > 0) {
        setSelectedBudget(availableBudgets[0].BudgetID);
      }
    } catch (err) {
      console.error('Error fetching budgets:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBudgetLines = async (budgetId) => {
    try {
      const [linesRes, overflowRes] = await Promise.all([
        budgetsAPI.getById(budgetId),
        budgetsAPI.getBudgetOverflow(budgetId).catch(() => ({ data: { overflowLines: [] } }))
      ]);
      setBudgetLines(linesRes.data.lines || []);
      setOverflowLines(overflowRes.data.overflowLines || []);
      
      // Fetch department GL accounts if budget has a department
      const budget = budgets.find(b => b.BudgetID === budgetId);
      if (budget && budget.DepartmentID) {
        await fetchDepartmentGLAccounts(budget.DepartmentID);
      } else {
        setDepartmentGLAccounts([]);
      }
    } catch (err) {
      console.error('Error fetching budget lines:', err);
    }
  };

  const handleResolveOverflow = async (lineId, status) => {
    try {
      await budgetsAPI.resolveOverflow(lineId, { status, notes: resolveNotes });
      setResolvingLineId(null);
      setResolveNotes('');
      if (selectedBudget) fetchBudgetLines(selectedBudget);
    } catch (err) {
      console.error('Error resolving overflow:', err);
      alert(err.response?.data?.message || 'Failed to resolve overflow line');
    }
  };

  const openCommittedModal = async (line) => {
    if (!selectedBudget || !line.CommittedAmount) return;
    setCommittedLoading(true);
    setCommittedLine({ line, pos: [], accruals: [] });
    try {
      const r = await budgetsAPI.getCommittedPOs(selectedBudget, line.BudgetLineID);
      setCommittedLine({ line, pos: r.data?.pos || [], accruals: r.data?.accruals || [] });
    } catch (_) {}
    finally { setCommittedLoading(false); }
  };

  const openReassignModal = async (line) => {
    setReassigningLine(line);
    setSelectedTargetLineId('');
    try {
      // Load planned budget lines for this company — all budgets
      const resp = await budgetsAPI.getAll({ page: 1, pageSize: 200 });
      const budgetsList = resp.data.items || [];
      const allLines = [];
      for (const b of budgetsList) {
        try {
          const bResp = await budgetsAPI.getById(b.BudgetID);
          const lines = (bResp.data.lines || []).filter(l => l.LineType !== 'Overflow');
          lines.forEach(l => allLines.push({ ...l, BudgetName: b.BudgetName }));
        } catch {}
      }
      setAllBudgetLines(allLines);
    } catch (err) {
      console.error('Error loading budget lines for reassign:', err);
      setAllBudgetLines([]);
    }
  };

  const handleReassign = async () => {
    if (!reassigningLine || !selectedTargetLineId) return;
    setReassigning(true);
    try {
      await budgetsAPI.reassignOverflow(reassigningLine.BudgetLineID, selectedTargetLineId);
      setReassigningLine(null);
      setSelectedTargetLineId('');
      if (selectedBudget) fetchBudgetLines(selectedBudget);
    } catch (err) {
      console.error('Error reassigning overflow:', err);
      alert(err.response?.data?.message || 'Failed to reassign overflow line');
    } finally {
      setReassigning(false);
    }
  };

  const fetchGlAccounts = async () => {
    try {
      const response = await glAccountsAPI.getAll();
      setGlAccounts(response.data?.glAccounts || response.data?.items || response.data || []);
    } catch (err) {
      console.error('Error fetching GL accounts:', err);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await apiClient.get('/departments');
      const data = response.data;
      setDepartments(data.departments || data || []);
    } catch (err) {
      console.error('Error fetching departments:', err);
      setDepartments([]);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await apiClient.get('/projects');
      const data = response.data;
      setProjects(data.projects || data.items || data || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchDepartmentGLAccounts = async (departmentId) => {
    try {
      const response = await apiClient.get(`/department-gl-accounts/${departmentId}/gl-accounts`);
      const data = response.data;
      setDepartmentGLAccounts(data.glAccounts || data || []);
    } catch (err) {
      console.error('Error fetching department GL accounts:', err);
      setDepartmentGLAccounts([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBudget) return;

    try {
      let submitData = { ...formData };

      // If creating a subscription, create it first then link
      if (formData.createSubscription && !editingLine) {
        const subPayload = {
          vendorId: formData.sub_vendorId,
          contractName: formData.sub_contractName || formData.description || 'Contract',
          contractNumber: formData.sub_contractNumber || null,
          paymentFrequency: formData.sub_paymentFrequency,
          paymentAmount: formData.sub_paymentAmount || formData.budgetedAmount,
          paymentDayOfMonth: formData.sub_paymentDayOfMonth || null,
          currency: 'GBP',
          startDate: formData.sub_startDate,
          endDate: formData.sub_endDate || null,
          renewalDate: formData.sub_renewalDate || null,
          noticePeriodDays: formData.sub_noticePeriodDays || null,
          autoRenew: formData.sub_autoRenew,
          glAccountId: formData.glAccountId || null,
          departmentId: formData.departmentId || null,
          description: formData.description || null,
          status: 'Active'
        };
        const subRes = await apiClient.post('/subscriptions', subPayload);
        submitData.subscriptionId = subRes.data.subscriptionId;
      }

      let result;
      if (editingLine) {
        await budgetsAPI.updateLine(selectedBudget, editingLine.BudgetLineID, submitData);
      } else {
        result = await budgetsAPI.createLine(selectedBudget, submitData);
        const carryOver = result?.data?.carryOver;
        if (carryOver && carryOver.nextYearMonths > 0) {
          if (carryOver.autoCreated) {
            alert(`Budget line created. £${carryOver.nextYearAmount.toFixed(2)} (${carryOver.nextYearMonths} month(s)) has been automatically carried over to the next financial year's budget.`);
          } else {
            alert(`Budget line created. Note: £${carryOver.nextYearAmount.toFixed(2)} (${carryOver.nextYearMonths} month(s)) extends into the next financial year. No matching budget was found for next year — please create one manually.`);
          }
        }
      }
      setShowModal(false);
      resetForm();
      fetchBudgetLines(selectedBudget);
    } catch (err) {
      console.error('Error saving budget line:', err);
      alert(err.response?.data?.message || 'Failed to save budget line');
    }
  };

  const handleDelete = async (lineId) => {
    if (!window.confirm('Are you sure you want to delete this budget line?')) return;
    
    try {
      await budgetsAPI.deleteLine(selectedBudget, lineId);
      fetchBudgetLines(selectedBudget);
    } catch (err) {
      console.error('Error deleting budget line:', err);
      alert('Failed to delete budget line');
    }
  };

  const openCreateModal = () => {
    resetForm();
    setEditingLine(null);
    setShowModal(true);
  };

  const openEditModal = (line) => {
    setEditingLine(line);
    setFormData({
      glAccountId: line.GLAccountID || '',
      description: line.Description || '',
      budgetedAmount: line.BudgetedAmount,
      departmentId: line.DepartmentID || '',
      projectId: line.ProjectID || '',
      costCentre: line.CostCentre || '',
      lineType: line.LineType || 'one_off',
      spreadMonths: line.SpreadMonths || '',
      spreadStartDate: line.SpreadStartDate ? line.SpreadStartDate.substring(0, 10) : '',
      spreadData: line.SpreadData || '',
      createSubscription: false,
      sub_vendorId: '',
      sub_contractName: '',
      sub_contractNumber: '',
      sub_paymentFrequency: 'Monthly',
      sub_paymentAmount: '',
      sub_paymentDayOfMonth: '',
      sub_startDate: '',
      sub_endDate: '',
      sub_renewalDate: '',
      sub_noticePeriodDays: '',
      sub_autoRenew: false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      glAccountId: '',
      description: '',
      budgetedAmount: '',
      departmentId: '',
      projectId: '',
      costCentre: '',
      lineType: 'one_off',
      spreadMonths: '',
      spreadStartDate: '',
      spreadData: '',
      createSubscription: false,
      sub_vendorId: '',
      sub_contractName: '',
      sub_contractNumber: '',
      sub_paymentFrequency: 'Monthly',
      sub_paymentAmount: '',
      sub_paymentDayOfMonth: '',
      sub_startDate: '',
      sub_endDate: '',
      sub_renewalDate: '',
      sub_noticePeriodDays: '',
      sub_autoRenew: false
    });
  };

  const fetchVendors = async () => {
    try {
      const res = await apiClient.get('/vendors?pageSize=200');
      setVendors(res.data?.vendors || res.data || []);
    } catch (err) {
      console.error('Failed to load vendors:', err);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!selectedBudget) {
      alert('Please select a budget first');
      return;
    }

    // Validate file type
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      alert('Please upload an Excel (.xlsx, .xls) or CSV file');
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await budgetsAPI.importLines(selectedBudget, formData);
      
      alert(`Successfully imported ${response.data.count} budget lines`);
      fetchBudgetLines(selectedBudget);
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error importing budget lines:', err);
      const errorMessage = err.response?.data?.message || 'Failed to import budget lines';
      const errors = err.response?.data?.errors;
      
      if (errors && errors.length > 0) {
        alert(`${errorMessage}\n\nErrors:\n${errors.join('\n')}`);
      } else {
        alert(errorMessage);
      }
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    // Create sample data
    const template = [
      ['Nominal Code', 'Description', 'Budgeted Amount'],
      ['4000', 'Office Supplies', '5000.00'],
      ['4100', 'Marketing Expenses', '15000.00'],
      ['4200', 'Software Licenses', '8000.00']
    ];

    // Convert to CSV
    const csv = template.map(row => row.join(',')).join('\n');
    
    // Create download link
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'budget_lines_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const currentBudget = budgets.find(b => b.BudgetID === selectedBudget);
  const totalBudgeted = budgetLines.reduce((sum, line) => sum + (line.BudgetedAmount || 0), 0);
  const totalSpent = budgetLines.reduce((sum, line) => sum + (line.SpentAmount || 0), 0);
  const totalCommitted = budgetLines.reduce((sum, line) => sum + (line.CommittedAmount || 0), 0);
  const totalRemaining = budgetLines.reduce((sum, line) => sum + (line.RemainingAmount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Back Button */}
      {searchParams.get('budgetId') && (
        <button
          onClick={() => navigate('/budget-headers')}
          className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Budgets
        </button>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Budget Lines</h1>
          <p className="text-gray-600 mt-1">View and manage budget line allocations</p>
        </div>
        {canManage && selectedBudget && (
          <div className="flex gap-2">
            <button 
              onClick={downloadTemplate} 
              className="btn-secondary flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download Template
            </button>
            <button 
              onClick={() => fileInputRef.current?.click()} 
              className="btn-secondary flex items-center gap-2"
              disabled={importing}
            >
              <Upload className="w-4 h-4" />
              {importing ? 'Importing...' : 'Import Lines'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button onClick={openCreateModal} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Add Line
            </button>
          </div>
        )}
      </div>

      {/* Budget Selector - Only show if not coming from URL parameter */}
      {!searchParams.get('budgetId') && (
        <div className="card">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Budget
          </label>
          <select
            value={selectedBudget || ''}
            onChange={(e) => setSelectedBudget(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select a budget...</option>
            {budgets.map(budget => (
              <option key={budget.BudgetID} value={budget.BudgetID}>
                {budget.BudgetName} - {budget.BudgetType || 'Departmental'} ({budget.YearLabel || budget.FinancialYear}) - {budget.Status}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Show selected budget info when coming from URL */}
      {searchParams.get('budgetId') && currentBudget && (
        <div className="card bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Viewing Lines For:</p>
              <p className="text-lg font-bold text-gray-900 mt-1">
                {currentBudget.BudgetName} - {currentBudget.BudgetType || 'Departmental'} ({currentBudget.YearLabel || currentBudget.FinancialYear})
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {selectedBudget && currentBudget && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Budget</p>
                <p className="text-2xl font-bold text-gray-900">£{(currentBudget.TotalBudget || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-indigo-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Allocated</p>
                <p className="text-2xl font-bold text-indigo-600">£{totalBudgeted.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
              </div>
              <FileText className="w-8 h-8 text-indigo-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-amber-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Spent</p>
                <p className="text-2xl font-bold text-orange-600">£{totalSpent.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-amber-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Committed</p>
                <p className="text-2xl font-bold text-yellow-600">£{totalCommitted.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-yellow-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Remaining</p>
                <p className={`text-2xl font-bold ${totalRemaining < 0 ? 'text-red-600' : 'text-green-600'}`}>£{totalRemaining.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
        </div>
      )}

      {/* Budget Lines Table */}
      {selectedBudget && (
        <div className="card">
          <div className="mb-3 flex items-center gap-2">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search budget lines..."
                value={linesSearch}
                onChange={e => setLinesSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              {(() => {
                const q = linesSearch.toLowerCase();
                const filteredLines = linesSearch
                  ? budgetLines.filter(l =>
                      (l.AccountCode || '').toLowerCase().includes(q) ||
                      (l.Description || '').toLowerCase().includes(q)
                    )
                  : budgetLines;
                const sortedLines = linesSortedData(filteredLines);
                const pagedLines = linesPagedData(sortedLines);
                return (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableTh field="AccountCode" sortBy={linesSortBy} sortDir={linesSortDir} onSort={linesHandleSort}>GL Account</SortableTh>
                    <SortableTh field="Description" sortBy={linesSortBy} sortDir={linesSortDir} onSort={linesHandleSort}>Description</SortableTh>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type / Spread</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dimensions</th>
                    <SortableTh field="BudgetedAmount" sortBy={linesSortBy} sortDir={linesSortDir} onSort={linesHandleSort} align="right">Budgeted</SortableTh>
                    <SortableTh field="SpentAmount" sortBy={linesSortBy} sortDir={linesSortDir} onSort={linesHandleSort} align="right">Spent</SortableTh>
                    <SortableTh field="CommittedAmount" sortBy={linesSortBy} sortDir={linesSortDir} onSort={linesHandleSort} align="right">Committed</SortableTh>
                    <SortableTh field="RemainingAmount" sortBy={linesSortBy} sortDir={linesSortDir} onSort={linesHandleSort} align="right">Remaining</SortableTh>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Utilization</th>
                    {canManage && (
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pagedLines.map((line) => {
                    const util = line.BudgetedAmount > 0
                      ? (((line.SpentAmount + line.CommittedAmount) / line.BudgetedAmount) * 100).toFixed(0)
                      : 0;
                    return (
                      <tr key={line.BudgetLineID} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {line.AccountCode && (
                            <div>
                              <span className="font-mono font-medium">{line.AccountCode}</span>
                              <div className="text-xs text-gray-600 mt-0.5">{line.AccountName}</div>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {line.Description}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div className="flex flex-col gap-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              line.LineType === 'new_service' ? 'bg-green-100 text-green-800' :
                              line.LineType === 'continuity' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {line.LineType === 'new_service' ? 'New Service' :
                               line.LineType === 'continuity' ? 'Continuity' : 'One-off'}
                            </span>
                            {line.SpreadMonths > 0 && (
                              <span className="text-xs text-gray-500">
                                {line.SpreadMonths}m from {line.SpreadStartDate ? new Date(line.SpreadStartDate).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '—'}
                              </span>
                            )}
                            {line.IsCarryOver && (
                              <span className="text-xs text-indigo-500 font-medium">↩ Carry-over</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div className="flex flex-col gap-1">
                            {line.DepartmentName && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                📁 {line.DepartmentName}
                              </span>
                            )}
                            {line.ProjectName && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                🎯 {line.ProjectName}
                              </span>
                            )}
                            {line.CostCentre && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                🏢 {line.CostCentre}
                              </span>
                            )}
                            {!line.DepartmentName && !line.ProjectName && !line.CostCentre && (
                              <span className="text-gray-400 italic">No dimensions</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-gray-900 font-medium">
                          £{(line.BudgetedAmount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-gray-600">
                          £{(line.SpentAmount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-gray-500">
                          {line.CommittedAmount > 0 ? (
                            <button
                              onClick={() => openCommittedModal(line)}
                              className="text-amber-600 hover:text-amber-800 hover:underline font-medium"
                              title="View committed POs"
                            >
                              £{(line.CommittedAmount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                            </button>
                          ) : (
                            <span>£0.00</span>
                          )}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-right font-medium ${line.RemainingAmount < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          £{(line.RemainingAmount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            util >= 100 ? 'bg-red-100 text-red-800' :
                            util >= 80 ? 'bg-orange-100 text-orange-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {util}%
                          </span>
                        </td>
                        {canManage && (
                          <td className="px-4 py-3 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => openEditModal(line)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Edit"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(line.BudgetLineID)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {budgetLines.length === 0 && (
                    <tr>
                      <td colSpan={canManage ? 10 : 9} className="px-4 py-8 text-center text-gray-500">
                        No budget lines found. {canManage && 'Click "Add Budget Line" to get started.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              );})()}
              <TablePagination {...linesPaginationProps(filteredLines.length)} />
            </div>
          )}
        </div>
      )}

      {/* Overflow / Unbudgeted Lines */}
      {selectedBudget && overflowLines.length > 0 && (
        <div className="bg-white rounded-lg shadow border border-red-200">
          <div className="px-4 py-3 border-b bg-gradient-to-r from-red-50 to-orange-50 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Unbudgeted Expenditure ({overflowLines.length})
            </h3>
            <span className="text-sm text-red-600 font-medium">
              £{overflowLines.reduce((s, l) => s + (l.SpentAmount || 0), 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
            </span>
          </div>
          <p className="px-4 pt-2 text-xs text-gray-500">
            These lines were auto-created when invoices were approved without a budget assignment. Use <strong>Reassign</strong> to move them to the correct budget line.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">GL Account</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Trigger Invoice</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Spent</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {overflowLines.map((line) => (
                  <tr key={line.BudgetLineID} className="hover:bg-red-50/30">
                    <td className="px-4 py-3 text-sm">
                      <span className="font-mono font-medium">{line.AccountCode}</span>
                      <div className="text-xs text-gray-500">{line.AccountName}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{line.Description}</td>
                    <td className="px-4 py-3 text-sm">
                      {line.TriggerInvoiceNumber ? (
                        <div>
                          <span className="flex items-center gap-1 text-blue-600">
                            <FileText className="w-3 h-3" /> {line.TriggerInvoiceNumber}
                          </span>
                          {line.TriggerVendorName && <div className="text-xs text-gray-400">{line.TriggerVendorName}</div>}
                        </div>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-red-600">
                      £{(line.SpentAmount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        line.OverflowStatus === 'Unresolved' ? 'bg-red-100 text-red-700' :
                        line.OverflowStatus === 'Acknowledged' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>{line.OverflowStatus}</span>
                      {line.ResolvedByName && (
                        <div className="text-xs text-gray-400 mt-0.5">by {line.ResolvedByName}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {line.OverflowStatus !== 'Resolved' && canManage && (
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => openReassignModal(line)}
                            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-medium"
                          >
                            Reassign
                          </button>
                          <button
                            onClick={() => handleResolveOverflow(line.BudgetLineID, 'Acknowledged')}
                            className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                            title="Acknowledge without reassigning"
                          >
                            Ack
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!selectedBudget && !loading && (
        <div className="card text-center py-12">
          <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Select a budget to view and manage budget lines</p>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-xl max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingLine ? 'Edit Budget Line' : 'Add Budget Line'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nominal Code (GL Account) *
                </label>
                <select
                  required
                  value={formData.glAccountId}
                  onChange={(e) => setFormData({ ...formData, glAccountId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select GL Account...</option>
                  {(departmentGLAccounts.length > 0 ? departmentGLAccounts : glAccounts).map(acc => (
                    <option key={acc.GLAccountID} value={acc.GLAccountID}>
                      {acc.AccountCode} - {acc.AccountName}
                    </option>
                  ))}
                </select>
                {departmentGLAccounts.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Showing GL accounts assigned to this budget's department
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Budget line description or notes"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Budgeted Amount (£) *
                </label>
                <input
                  type="number"
                  required
                  step="0.01"
                  min="0"
                  value={formData.budgetedAmount}
                  onChange={(e) => setFormData({ ...formData, budgetedAmount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>

              {/* Spend Type & Spread */}
              <div className="pt-3 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Spend Type & Spread</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Spend Type</label>
                    <select
                      value={formData.lineType}
                      onChange={(e) => setFormData({ ...formData, lineType: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="one_off">One-off purchase / payment</option>
                      <option value="new_service">New service / subscription</option>
                      <option value="continuity">Continuity (ongoing cost)</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Spread start date</label>
                      <input
                        type="date"
                        value={formData.spreadStartDate}
                        onChange={(e) => setFormData({ ...formData, spreadStartDate: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Spread over (months)</label>
                      <input
                        type="number"
                        min="1"
                        max="60"
                        value={formData.spreadMonths}
                        onChange={(e) => setFormData({ ...formData, spreadMonths: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g. 12"
                      />
                    </div>
                  </div>
                  {formData.spreadMonths > 0 && formData.budgetedAmount > 0 && (
                    <p className="text-xs text-indigo-600 bg-indigo-50 rounded px-2 py-1">
                      ≈ £{(parseFloat(formData.budgetedAmount) / parseInt(formData.spreadMonths)).toFixed(2)} / month (equal split)
                    </p>
                  )}
                </div>
              </div>

              {/* Subscription / Contract */}
              {!editingLine && (
                <div className="pt-3 border-t border-gray-200">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={formData.createSubscription}
                      onChange={(e) => setFormData({ ...formData, createSubscription: e.target.checked })}
                      className="w-4 h-4 rounded text-blue-600"
                    />
                    <span className="text-sm font-semibold text-gray-700">This is a recurring subscription / contract</span>
                  </label>
                  <p className="text-xs text-gray-400 mt-1 ml-6">Creates a linked subscription record for renewal reminders &amp; payment scheduling.</p>

                  {formData.createSubscription && (
                    <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Vendor *</label>
                          <select
                            required={formData.createSubscription}
                            value={formData.sub_vendorId}
                            onChange={(e) => setFormData({ ...formData, sub_vendorId: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select vendor...</option>
                            {vendors.filter(v => v.IsActive !== false).map(v => (
                              <option key={v.VendorID} value={v.VendorID}>{v.VendorName}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Contract Name *</label>
                          <input
                            type="text"
                            required={formData.createSubscription}
                            value={formData.sub_contractName}
                            onChange={(e) => setFormData({ ...formData, sub_contractName: e.target.value })}
                            placeholder={formData.description || 'e.g. Microsoft 365'}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Contract Number</label>
                          <input
                            type="text"
                            value={formData.sub_contractNumber}
                            onChange={(e) => setFormData({ ...formData, sub_contractNumber: e.target.value })}
                            placeholder="e.g. CTR-2025-001"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Payment Frequency *</label>
                          <select
                            value={formData.sub_paymentFrequency}
                            onChange={(e) => setFormData({ ...formData, sub_paymentFrequency: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="Monthly">Monthly</option>
                            <option value="Annually">Annually</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Payment Amount (£) *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            required={formData.createSubscription}
                            value={formData.sub_paymentAmount}
                            onChange={(e) => setFormData({ ...formData, sub_paymentAmount: e.target.value })}
                            placeholder={formData.budgetedAmount || '0.00'}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                          />
                          <p className="text-[10px] text-gray-400 mt-0.5">Per-payment amount (not annual total)</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Payment Day of Month</label>
                          <input
                            type="number"
                            min="1"
                            max="31"
                            value={formData.sub_paymentDayOfMonth}
                            onChange={(e) => setFormData({ ...formData, sub_paymentDayOfMonth: e.target.value })}
                            placeholder="e.g. 1"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Start Date *</label>
                          <input
                            type="date"
                            required={formData.createSubscription}
                            value={formData.sub_startDate}
                            onChange={(e) => setFormData({ ...formData, sub_startDate: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                          <input
                            type="date"
                            value={formData.sub_endDate}
                            onChange={(e) => setFormData({ ...formData, sub_endDate: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Renewal Date</label>
                          <input
                            type="date"
                            value={formData.sub_renewalDate}
                            onChange={(e) => setFormData({ ...formData, sub_renewalDate: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 items-end">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Notice Period (days)</label>
                          <input
                            type="number"
                            min="0"
                            value={formData.sub_noticePeriodDays}
                            onChange={(e) => setFormData({ ...formData, sub_noticePeriodDays: e.target.value })}
                            placeholder="e.g. 30"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                          />
                          <p className="text-[10px] text-gray-400 mt-0.5">Days before renewal you must cancel</p>
                        </div>
                        <div className="pb-1">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.sub_autoRenew}
                              onChange={(e) => setFormData({ ...formData, sub_autoRenew: e.target.checked })}
                              className="w-4 h-4 rounded text-blue-600"
                            />
                            <span className="text-sm font-medium text-gray-700">Auto-renew</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Dimension Fields */}
              <div className="pt-3 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Dimensions (Optional)</h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Department
                    </label>
                    <select
                      value={formData.departmentId}
                      onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">No department</option>
                      {departments.map(dept => (
                        <option key={dept.DepartmentID} value={dept.DepartmentID}>
                          {dept.DepartmentName || dept.Name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Project
                    </label>
                    <select
                      value={formData.projectId}
                      onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">No project</option>
                      {projects.map(proj => (
                        <option key={proj.ProjectID} value={proj.ProjectID}>
                          {proj.ProjectCode} - {proj.ProjectName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cost Centre
                    </label>
                    <input
                      type="text"
                      value={formData.costCentre}
                      onChange={(e) => setFormData({ ...formData, costCentre: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., HQ-Floor1, WH-001"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-primary flex-1">
                  {editingLine ? 'Update' : 'Add'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reassign Overflow Modal */}
      {/* Committed PO Drill-Down Modal */}
      {committedLine && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Committed Purchase Orders</h2>
                <p className="text-sm text-gray-500">{committedLine.line.AccountCode} — {committedLine.line.Description}</p>
              </div>
              <button onClick={() => setCommittedLine(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-y-auto flex-1 px-6 py-4">
              {committedLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                  <span className="ml-2 text-sm text-gray-500">Loading…</span>
                </div>
              ) : committedLine.pos.length === 0 && committedLine.accruals.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No committed items found for this budget line.</p>
              ) : (
                <div className="space-y-5">
                  {committedLine.pos.length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Purchase Orders</h3>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-gray-400 uppercase border-b border-gray-100">
                            <th className="text-left py-2 pr-3">PO</th>
                            <th className="text-left py-2 pr-3">Vendor</th>
                            <th className="text-left py-2 pr-3">Line Description</th>
                            <th className="text-left py-2 pr-3">Status</th>
                            <th className="text-right py-2">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {committedLine.pos.map(p => (
                            <tr key={p.POLineID} className="hover:bg-gray-50">
                              <td className="py-2 pr-3">
                                <button onClick={() => { setCommittedLine(null); navigate(`/purchase-orders/${p.POID}`); }} className="font-mono text-indigo-600 hover:underline text-xs">{p.PONumber}</button>
                                {p.PODate && <div className="text-xs text-gray-400">{new Date(p.PODate).toLocaleDateString('en-GB')}</div>}
                              </td>
                              <td className="py-2 pr-3 text-gray-700">{p.VendorName || '—'}</td>
                              <td className="py-2 pr-3 text-gray-600">{p.LineDescription || '—'}</td>
                              <td className="py-2 pr-3">
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${p.POStatus === 'Approved' ? 'bg-green-100 text-green-800' : p.POStatus === 'Open' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`}>{p.POStatus === 'Open' ? 'Partial' : p.POStatus}</span>
                              </td>
                              <td className="py-2 text-right font-medium text-gray-900">
                                £{(p.LineTotal || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                                {p.POStatus === 'Open' && p.OriginalLineTotal > p.LineTotal && (
                                  <div className="text-xs text-gray-400">of £{(p.OriginalLineTotal || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })} total</div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {committedLine.accruals.length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Accruals</h3>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-gray-400 uppercase border-b border-gray-100">
                            <th className="text-left py-2 pr-3">Description</th>
                            <th className="text-left py-2 pr-3">Vendor</th>
                            <th className="text-left py-2 pr-3">Date</th>
                            <th className="text-left py-2 pr-3">Status</th>
                            <th className="text-right py-2">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {committedLine.accruals.map(a => (
                            <tr key={a.AccrualID} className="hover:bg-gray-50">
                              <td className="py-2 pr-3 text-gray-700">{a.Description || '—'}</td>
                              <td className="py-2 pr-3 text-gray-600">{a.VendorName || '—'}</td>
                              <td className="py-2 pr-3 text-gray-500">{a.AccrualDate ? new Date(a.AccrualDate).toLocaleDateString('en-GB') : '—'}</td>
                              <td className="py-2 pr-3">
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${a.Status === 'Matched' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}`}>{a.Status}</span>
                              </td>
                              <td className="py-2 text-right font-medium text-gray-900">£{(a.EstimatedAmount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="border-t-2 border-gray-200 pt-2 flex justify-end">
                    <span className="text-sm font-semibold text-gray-700 mr-4">Total Committed</span>
                    <span className="font-bold text-amber-700">
                      £{(committedLine.pos.reduce((s, p) => s + (p.LineTotal || 0), 0) + committedLine.accruals.reduce((s, a) => s + (a.EstimatedAmount || 0), 0)).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {reassigningLine && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Reassign Overflow Line</h2>
              <button onClick={() => setReassigningLine(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm font-medium text-gray-900">
                {reassigningLine.AccountCode} — {reassigningLine.AccountName}
              </p>
              <p className="text-sm text-gray-600">{reassigningLine.Description}</p>
              <p className="text-sm font-semibold text-red-600 mt-1">
                Spent: £{(reassigningLine.SpentAmount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Move to Budget Line:
              </label>
              {allBudgetLines.length === 0 ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-sm text-gray-500">Loading budget lines...</span>
                </div>
              ) : (
                <select
                  value={selectedTargetLineId}
                  onChange={(e) => setSelectedTargetLineId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select target budget line...</option>
                  {allBudgetLines.map(bl => (
                    <option key={bl.BudgetLineID} value={bl.BudgetLineID}>
                      [{bl.BudgetName}] {bl.AccountCode} {bl.AccountName} — Budgeted: £{(bl.BudgetedAmount || 0).toLocaleString()} / Spent: £{(bl.SpentAmount || 0).toLocaleString()}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleReassign}
                disabled={!selectedTargetLineId || reassigning}
                className="btn-primary flex-1 disabled:opacity-50"
              >
                {reassigning ? 'Reassigning...' : 'Reassign'}
              </button>
              <button
                onClick={() => setReassigningLine(null)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
